import type { ErrorCopyFor, ExplainedError } from "@amtana/browser";
import { phrase } from "@amtana/i18n/catalog";
import type { Catalogs, Translation } from "@amtana/i18n/catalog";
import type { ONBOARDING_ERROR_CODES } from "@amtana/onboarding-contract";

// What a person reads when onboarding refuses something (packages/module ADR-001),
// in English and Spanish, read in the active language when the error shows.

const EN = {
  lockedTitle: "Your first analysis has already started",
  lockedDescription: "Its leads keep arriving; see them on the platform.",
  targetNotFoundTitle: "We couldn't find that",
  targetNotFoundDescription: "Check the spelling and try again.",
  termsRequiredTitle: "Please accept the Terms first",
  termsRequiredDescription: "Tick the box to agree to the Terms of Service, then continue.",
  termsOutdatedTitle: "Our Terms changed",
  termsOutdatedDescription: "Reload the page to read the new Terms, then accept them.",
} as const;

const ES: Translation<typeof EN> = {
  lockedTitle: "Tu primer análisis ya empezó",
  lockedDescription: "Sus leads siguen llegando: míralos en la plataforma.",
  targetNotFoundTitle: "No pudimos encontrar eso",
  targetNotFoundDescription: "Revisa la ortografía e inténtalo de nuevo.",
  termsRequiredTitle: "Primero acepta los Términos",
  termsRequiredDescription: "Marca la casilla para aceptar los Términos del servicio y continúa.",
  termsOutdatedTitle: "Nuestros Términos cambiaron",
  termsOutdatedDescription: "Recarga la página para leer los nuevos Términos y acéptalos.",
};

export const ONBOARDING_ERROR_CATALOGS: Catalogs<typeof EN> = { en: EN, es: ES };

export const ONBOARDING_ERROR_COPY = {
  onboarding_locked: {
    title: phrase(ONBOARDING_ERROR_CATALOGS, "lockedTitle"),
    description: phrase(ONBOARDING_ERROR_CATALOGS, "lockedDescription"),
  },
  onboarding_target_not_found: {
    title: phrase(ONBOARDING_ERROR_CATALOGS, "targetNotFoundTitle"),
    description: phrase(ONBOARDING_ERROR_CATALOGS, "targetNotFoundDescription"),
  },
  onboarding_terms_required: {
    title: phrase(ONBOARDING_ERROR_CATALOGS, "termsRequiredTitle"),
    description: phrase(ONBOARDING_ERROR_CATALOGS, "termsRequiredDescription"),
  },
  onboarding_terms_outdated: {
    title: phrase(ONBOARDING_ERROR_CATALOGS, "termsOutdatedTitle"),
    description: phrase(ONBOARDING_ERROR_CATALOGS, "termsOutdatedDescription"),
  },
} satisfies ErrorCopyFor<typeof ONBOARDING_ERROR_CODES>;

/** A failed `startFirstAnalysis` as one line under the form's field (D947): the specific reason
 * when a handled code explains one (account not found, locked), the generic action otherwise.
 * Read inline, next to the input, the same place a validation error already shows; the mutation
 * itself never escalates to the screen's error boundary over this (packages/browser ADR-001). */
export function startFailureText(explained: Pick<ExplainedError, "title" | "description">): string {
  return explained.description || explained.title;
}
